import type { ReactElement, ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

import { BdsCol, BdsLayout } from "@buildertrend/components";

import { ListActionBar } from "commonComponents/utilities/ListActionBar/ListActionBar";

import "./ListPageFooter.less";

interface IListPageFooterProps {
    children: ReactNode;
    /**
     * Wraps the footer in a sticky ListActionBar pinned to the bottom of the viewport.
     * @default false
     */
    withListActionBar?: boolean;
    id?: string;
}

export interface IListPageFooterLayoutProps {
    /**
     * Left slot — typically ManageSavedViews (wrapped in ShowOnPortal if builder-only).
     * Renders an empty column if undefined.
     */
    viewControls?: ReactNode;
    /**
     * Optional center content rendered between viewControls and paginationControls.
     */
    children?: ReactNode;
    /**
     * Right slot — typically BTPagination or BTPaginationHistory.
     * Renders an empty column if undefined.
     */
    paginationControls?: ReactNode;
    testid?: string;
    className?: string;
}

/**
 * Standard 1-and-2 column layout helper for list page footers.
 *
 * Pass fully-configured components into the named slots; this component only handles positioning.
 * Use ShowOnPortal in the caller if viewControls should be builder-only.
 *
 * @example
 * <ListPageFooter.Layout
 *   viewControls={
 *     <ShowOnPortal builder render={() => <ManageSavedViews {...manageSavedViewsProps} />} />
 *   }
 *   paginationControls={
 *     <BTPagination entityType={entityType} pagingData={pagingData} onChange={handlePagingChange} id="EntityPagingBottom" />
 *   }
 * />
 */
const Layout = ({
    viewControls,
    children,
    paginationControls,
    testid,
    className,
}: IListPageFooterLayoutProps): ReactElement => (
    <BdsLayout
        displayType="1and2"
        className={cn("ListPageFooterLayout", className)}
        {...(testid && { testid })}
        gap={[
            { dir: "hz", break: "min", size: "1" },
            { dir: "vt", break: "min", size: "1" },
        ]}
    >
        <BdsCol>{viewControls}</BdsCol>
        {children && <BdsCol>{children}</BdsCol>}
        <BdsCol>{paginationControls}</BdsCol>
    </BdsLayout>
);

/**
 * ListPageFooter - Outer container for list page footers.
 *
 * Handles sticky positioning via ListActionBar. Pair with ListPageFooter.Layout for the
 * standard two-column view-controls / pagination layout.
 *
 * @example
 * <ListPageFooter withListActionBar id="EntityListFooterSection">
 *   <ListPageFooter.Layout
 *     testid="EntityListFooterSection"
 *     viewControls={<ShowOnPortal builder render={() => <ManageSavedViews {...props} />} />}
 *     paginationControls={<BTPagination {...props} id="EntityPagingBottom" />}
 *   />
 * </ListPageFooter>
 */
const ListPageFooterComponent = ({
    children,
    withListActionBar = false,
    id,
}: IListPageFooterProps): ReactElement => {
    if (withListActionBar) {
        return (
            <ListActionBar sticky="bottom" {...(id && { id })}>
                {children}
            </ListActionBar>
        );
    }

    return <>{children}</>;
};

export const ListPageFooter = Object.assign(ListPageFooterComponent, { Layout });
